import React from "react";
import type { CollapseProps, DescriptionsProps, MenuProps, TabsProps } from "antd";

type MenuItemType = NonNullable<MenuProps["items"]>[number];
type TabItemType = NonNullable<TabsProps["items"]>[number];
type DescriptionItemType = NonNullable<DescriptionsProps["items"]>[number];
type CollapseItemType = NonNullable<CollapseProps["items"]>[number];

export const MenuItem: React.FC<any> = () => null;
export const MenuItemGroup: React.FC<any> = () => null;
export const MenuDivider: React.FC<any> = () => null;
export const SubMenu: React.FC<any> = () => null;
export const TabItem: React.FC<any> = () => null;
export const DescriptionItem: React.FC<any> = () => null;
export const CollapsePanel: React.FC<any> = () => null;

function getElementKey(element: React.ReactElement, fallback?: React.Key) {
    return element.key ?? fallback;
}

function collectItems<T>(
    children: React.ReactNode,
    mapper: (element: React.ReactElement, index: number) => T | T[] | null,
): T[] {
    const items: T[] = [];

    React.Children.forEach(children, (child, index) => {
        if (child === null || child === undefined || child === false) return;
        if (!React.isValidElement(child)) return;

        if (child.type === React.Fragment) {
            items.push(...collectItems(child.props.children, mapper));
            return;
        }

        const item = mapper(child, index);

        if (Array.isArray(item)) {
            items.push(...item.filter(Boolean));
            return;
        }

        if (item) {
            items.push(item);
        }
    });

    return items;
}

export function buildMenuItems(children: React.ReactNode): MenuProps["items"] {
    return collectItems<MenuItemType>(children, (element, index) => {
        if (element.type === MenuDivider) {
            const { children: _children, ...restProps } = element.props;
            return { type: "divider", key: getElementKey(element, `divider-${index}`), ...restProps };
        }

        if (element.type === MenuItemGroup) {
            const { children: groupChildren, title, ...restProps } = element.props;
            return {
                type: "group",
                key: getElementKey(element, `group-${index}`),
                label: title,
                children: buildMenuItems(groupChildren),
                ...restProps,
            } as MenuItemType;
        }

        if (element.type === SubMenu) {
            const { children: subMenuChildren, title, eventKey, ...restProps } = element.props;
            return {
                key: getElementKey(element, eventKey ?? `submenu-${index}`),
                label: title,
                children: buildMenuItems(subMenuChildren),
                ...restProps,
            } as MenuItemType;
        }

        if (element.type === MenuItem) {
            const { children: label, eventKey, ...restProps } = element.props;
            return {
                key: getElementKey(element, eventKey ?? `item-${index}`),
                label,
                ...restProps,
            } as MenuItemType;
        }

        return null;
    });
}

export function buildTabsItems(children: React.ReactNode): TabsProps["items"] {
    return collectItems<TabItemType>(children, (element, index) => {
        if (element.type !== TabItem) return null;

        const {
            children: itemChildren,
            tab,
            tabKey,
            destroyOnHidden,
            ...restProps
        } = element.props;

        return {
            key: getElementKey(element, tabKey ?? `tab-${index}`),
            label: tab,
            children: itemChildren,
            destroyOnHidden,
            ...restProps,
        } as TabItemType;
    });
}

export function buildDescriptionsItems(children: React.ReactNode): DescriptionsProps["items"] {
    return collectItems<DescriptionItemType>(children, (element, index) => {
        if (element.type !== DescriptionItem) return null;

        const { children: itemChildren, ...restProps } = element.props;

        return {
            key: getElementKey(element, `description-${index}`),
            children: itemChildren,
            ...restProps,
        } as DescriptionItemType;
    });
}

export function buildCollapseItems(children: React.ReactNode): CollapseProps["items"] {
    return collectItems<CollapseItemType>(children, (element, index) => {
        if (element.type !== CollapsePanel) return null;

        const { children: itemChildren, header, title, ...restProps } = element.props;

        return {
            key: getElementKey(element, `collapse-${index}`),
            label: header ?? title,
            children: itemChildren,
            ...restProps,
        } as CollapseItemType;
    });
}
